import React from 'react';
import { Button } from '@mui/material';
import Info from '@mui/icons-material/InfoOutlined';
import { useLocalizationContext } from '../../localization/LocalizationContext';
import { makeStyles } from '@mui/styles';
import Popover from '@mui/material/Popover';
import { useSelector } from 'react-redux';
import { getLocale } from '../../store/selectors/settings';
import { items, WhatsNewItems } from './WhatsNewItems';
import { getShortLanguageCode } from '../../utils/locale';
import { useLocalStorage } from '../../utils/useLocalStorage';

import { Theme } from '../../themes/types';
import { layoutTokens } from '../../themes/layoutTokens';
import { getEasterTheme } from '../../themes/easterTheme';
import { useAppTheme } from '../../themes/useAppTheme';
import { getRecentWhatsNewItems } from './whatsNewUtils';

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        position: 'relative' as const,
        marginRight: 20
    },
    paper: {
        padding: 0,
        background: theme.palette.contentPaperBackground,
        position: 'absolute' as const,
        right: 20,
        top: 45,
        color: theme.palette.contentPaperText,
        minWidth: '248px'
    },
    title: {
        fontSize: 12,
        padding: '15px'
    },
    whatsNewButton: {
        padding: '6px 10px',
        fontWeight: 'semibold',
        backgroundColor: theme.palette.whatsNewButtonInactiveBg,
        color: theme.palette.whatsNewButtonInactiveText,
        borderColor: theme.palette.whatsNewButtonInactiveBorder ?? theme.palette.whatsNewButtonInactiveBg,
        '&:hover': {
            backgroundColor: theme.palette.buttonAccentSoftBg,
            borderColor: theme.palette.whatsNewButtonInactiveBorder ?? theme.palette.whatsNewButtonInactiveBg
        },
        '&:active': {
            backgroundColor: theme.palette.buttonAccentSoftActiveBg,
            borderColor: theme.palette.whatsNewButtonInactiveBorder ?? theme.palette.whatsNewButtonInactiveBg
        }
    },
    whatsNewButtonActive: {
        padding: '6px 10px',
        fontWeight: 'semibold',
        backgroundColor: theme.palette.whatsNewButtonActiveBg,
        color: theme.palette.whatsNewButtonActiveText,
        borderColor: theme.palette.whatsNewButtonActiveBg,
        '&:hover': {
            backgroundColor: theme.palette.buttonAccentHoverBg,
            color: theme.palette.whatsNewButtonActiveText,
            borderColor: theme.palette.buttonAccentHoverBg
        },
        '&:active': {
            backgroundColor: theme.palette.buttonAccentHoverBg,
            borderColor: theme.palette.whatsNewButtonActiveBg
        }
    },
    markAllAsReadButton: {
        color: theme.palette.buttonAccentBg,
        '&:hover': {
            backgroundColor: theme.palette.buttonAccentSoftBg
        },
        '&:active': {
            backgroundColor: theme.palette.buttonAccentSoftActiveBg
        }
    },
    markAllAsReadContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '6px 10px'
    }
}));

// MIGRATION @todo remove later
if (localStorage.getItem('whats-new-opened')) {
    localStorage.setItem('keep.whats-new-items', localStorage.getItem('whats-new-opened') ?? '[]');
    localStorage.removeItem('whats-new-opened');
}
//

export const WhatsNew: React.FC = () => {
    const localization = useLocalizationContext();
    const classes = useStyles();
    const theme = useAppTheme();
    const easterTheme = getEasterTheme(theme.palette.isDarkTheme);
    const easterInactiveSx = easterTheme.enabled
        ? {
              color: easterTheme.whatsNewInactive.color,
              borderColor: easterTheme.whatsNewInactive.borderColor,
              backgroundColor: easterTheme.whatsNewInactive.backgroundColor,
              '& .MuiSvgIcon-root': {
                  color: easterTheme.whatsNewInactive.color
              },
              '& span': {
                  color: easterTheme.whatsNewInactive.color
              },
              '&:hover': {
                  backgroundColor: easterTheme.whatsNewInactive.hoverBackgroundColor,
                  borderColor: easterTheme.whatsNewInactive.hoverBorderColor
              }
          }
        : undefined;
    const easterActiveSx = easterTheme.enabled
        ? {
              color: easterTheme.whatsNewActive.color,
              borderColor: 'transparent',
              background: easterTheme.whatsNewActive.background,
              boxShadow: easterTheme.whatsNewActive.boxShadow,
              '& .MuiSvgIcon-root': {
                  color: easterTheme.whatsNewActive.color
              },
              '& span': {
                  color: easterTheme.whatsNewActive.color
              },
              '&:hover': {
                  background: easterTheme.whatsNewActive.hoverBackground,
                  color: easterTheme.whatsNewActive.color,
                  borderColor: 'transparent'
              },
              '&:active': {
                  background: easterTheme.whatsNewActive.activeBackground,
                  borderColor: 'transparent'
              }
          }
        : undefined;

    const [opened, setOpened] = useLocalStorage<string[]>('keep.whats-new-items', []);
    const onOpen = (key: string) => {
        if (!opened.includes(key)) {
            setOpened([...opened, key]);
        }
    };

    const locale = useSelector(state => getLocale(state));
    const lang = getShortLanguageCode(locale);

    const list = getRecentWhatsNewItems(items[lang] ?? []);

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const markAllAsRead = () => {
        setOpened(list.map(item => item.key));
        handleClose();
    };

    const hasUnread = list.filter(item => !opened.includes(item.key)).length > 0;

    if (list.length === 0) {
        return null;
    }

    return (
        <div className={classes.container} data-test="whats-new">
            <Button
                className={hasUnread ? classes.whatsNewButtonActive : classes.whatsNewButton}
                onClick={handleClick}
                variant="outlined"
                data-test="whats-new-button"
                sx={hasUnread ? easterActiveSx : easterInactiveSx}
            >
                <Info sx={{ mr: '6px', width: 16, height: 16 }} />
                <span>{localization.getText('whatsNew.title')}</span>
            </Button>

            <Popover
                id={'whats-new-popover'}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                data-test="whats-new-popover"
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: (theme: any) => theme.palette.contentPaperBackground,
                            color: (theme: any) => theme.palette.contentPaperText,
                            border: (theme: any) => `1px solid ${theme.palette.contentPaperBorder}`,
                            borderRadius: `${layoutTokens.borderRadiusPanel}px`,
                            boxShadow: (theme: any) => theme.shadows[1],
                            backdropFilter: `blur(${layoutTokens.panelBlur}px)`,
                            WebkitBackdropFilter: `blur(${layoutTokens.panelBlur}px)`
                        }
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <WhatsNewItems lang={lang} opened={opened} onOpen={onOpen} />
                <div className={classes.markAllAsReadContainer}>
                    <Button variant="text" size="small" onClick={markAllAsRead} className={classes.markAllAsReadButton}>
                        {localization.getText('whatsNew.markAllAsRead')}
                    </Button>
                </div>
            </Popover>
        </div>
    );
};
